import { Lead, PartialLead } from '@superfunnel/types';
import MenuTabs, { MenuTab } from '@superfunnel/ui-core/MenuTabs';
import Button from '@superfunnel/ui-core/v2/Button';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/v2/Tabs';
import { collection, query, where } from 'firebase/firestore';
import { Import } from 'iconoir-react';
import { useContext, useMemo, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { db } from '../../Firebase';
import ExportLeadsPopover from '../../popovers/ExportLeadsPopover';
import { AnalyticsConfig } from '../../types/overview';
import { emptyConverter } from '../../utils';
import { extractTextFromQuillTrimmed } from '../../utils/quill';
import StepperAnalytics from '../analytics/StepperAnalytics';
import DashboardAnalyticsRangeSelector from '../DashboardAnalyticsRangeSelector';
import { LeadsTable } from '../LeadsTable';
import ChartsGrid from '../Overview/ChartsGrid';
import { PartialLeadsTable } from '../PartialLeadsTable';
import { useHtmlStepperEditor } from './HtmlStepperEditorContext';

type ResultsTab = 'analytics' | 'leads';
type LeadViewType = 'leads' | 'partialLeads';

/**
 * Quiz-specific analytics configuration.
 * Simplified version focused on key metrics with formId grouping instead of variants.
 */
const quizAnalyticsConfig: AnalyticsConfig = [
  {
    id: 'kpi_visitors',
    title: 'Total Visitors',
    kind: 'metric',
    metrics: ['visitors'],
    aggregate: 'sum',
    comparison: true,
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 4 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'kpi_conversions',
    title: 'Total Conversions',
    kind: 'metric',
    metrics: ['conversions'],
    aggregate: 'sum',
    comparison: true,
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 4 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'kpi_cvr',
    title: 'Avg Conversion Rate',
    kind: 'metric',
    metrics: ['cvr'],
    aggregate: 'avg',
    comparison: true,
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 4 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'top_forms',
    title: 'Top Forms',
    kind: 'bar-horizontal',
    metrics: ['visitors'],
    segment: 'formId',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 6 },
    },
  },
  {
    id: 'top_referrers',
    title: 'Top Referrers',
    kind: 'bar-horizontal',
    metrics: ['visitors'],
    segment: 'referrerDomain',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 6 },
    },
  },
  {
    id: 'trend_main',
    title: 'Trends Over Time',
    kind: 'area',
    metrics: ['visitors', 'conversions', 'cvr'],
    segment: 'day',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 12 },
      desktop: { cols: 12 },
    },
  },
  {
    id: 'breakdown_device',
    title: 'Device Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'device',
    minLegendItems: ['mobile', 'desktop', 'tablet'],
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_country',
    title: 'Country Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'country',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_browser',
    title: 'Browser Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'browser',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
];

/**
 * Results tab content for HtmlStepperEditor.
 * Displays overview analytics charts and stepper form analytics.
 */
export const HtmlStepperEditorResults = () => {
  const { selectedAccount } = useContext(UserContext);
  const { state, data } = useHtmlStepperEditor();
  const { analyticsFilters } = state;
  const { dynamicPageId, forms } = data;

  const [activeTab, setActiveTab] = useState<ResultsTab>('analytics');
  const [leadViewType, setLeadViewType] = useState<LeadViewType>('leads');

  // Map forms from QueryDocumentSnapshot to { id, name } array
  const formsArray = useMemo(() => {
    return forms.map((f) => ({
      id: f.id,
      name: extractTextFromQuillTrimmed(f.data().name),
    }));
  }, [forms]);

  // Create a form name translator for chart labels
  const translateFormLabel = useMemo(() => {
    const formMap = new Map(formsArray.map((f) => [f.id, f.name]));
    return (segment: string | undefined, value: string): string => {
      if (segment === 'formId') {
        return formMap.get(value) || value;
      }
      return value;
    };
  }, [formsArray]);

  // Leads query
  const leadsQuery = useMemo(() => {
    if (!selectedAccount || !dynamicPageId) return;
    return query(
      collection(db, 'accounts', selectedAccount.id, 'leads').withConverter(
        emptyConverter<Lead>(),
      ),
      where('campaignId', '==', dynamicPageId),
    );
  }, [selectedAccount, dynamicPageId]);

  // Partial leads query
  const partialLeadsQuery = useMemo(() => {
    if (!selectedAccount || !dynamicPageId) return;
    return query(
      collection(
        db,
        'accounts',
        selectedAccount.id,
        'partialLeads',
      ).withConverter(emptyConverter<PartialLead>()),
      where('campaignId', '==', dynamicPageId),
    );
  }, [selectedAccount, dynamicPageId]);

  const tabs: MenuTab[] = useMemo(
    () => [
      {
        id: 'analytics',
        name: 'Analytics',
        current: activeTab === 'analytics',
      },
      { id: 'leads', name: 'Leads', current: activeTab === 'leads' },
    ],
    [activeTab],
  );

  // dynamicPageId is guaranteed non-null when quiz is loaded
  // Show loading state if still fetching
  if (!dynamicPageId) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header with tabs and date range picker */}
      <div className="border-base-200 flex h-12 items-end justify-between border-b bg-white ps-6 pe-2">
        <MenuTabs
          tabs={tabs}
          onTabClick={(id) => setActiveTab(id as ResultsTab)}
          className="border-0"
          size="sm"
        />
        <div className="pb-2">
          <DashboardAnalyticsRangeSelector size="small" />
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto p-6 pe-4">
        {activeTab === 'analytics' && (
          <>
            {/* Overview Analytics */}
            <ChartsGrid
              config={quizAnalyticsConfig}
              analyticsFilters={analyticsFilters}
              campaignId={dynamicPageId}
              translateSegmentLabel={translateFormLabel}
            />

            {/* Stepper Form Analytics */}
            <div className="mt-6">
              <StepperAnalytics
                dynamicPageId={dynamicPageId}
                forms={formsArray}
                analyticsFilters={analyticsFilters}
                title="Form Step Analytics"
              />
            </div>
          </>
        )}

        {activeTab === 'leads' && (
          <div className="flex h-full flex-col gap-4">
            {/* Leads header */}
            <div className="flex items-center justify-between">
              <Tabs
                value={leadViewType}
                onValueChange={(value) =>
                  setLeadViewType(value as LeadViewType)
                }
              >
                <TabsList>
                  <TabsTrigger value="leads">Leads</TabsTrigger>
                  <TabsTrigger value="partialLeads">Partial Leads</TabsTrigger>
                </TabsList>
              </Tabs>
              <ExportLeadsPopover
                defaultDynamicPageIds={
                  dynamicPageId ? [dynamicPageId] : undefined
                }
                defaultLeadType={leadViewType}
              >
                <Button
                  variant="outline"
                  color="white"
                  size="icon-lg"
                  className="gap-2"
                >
                  <Import className="h-4 w-4" />
                </Button>
              </ExportLeadsPopover>
            </div>

            {/* Leads table */}
            <div className="relative flex-1">
              {leadViewType === 'leads' && leadsQuery && (
                <LeadsTable query={leadsQuery} />
              )}
              {leadViewType === 'partialLeads' && partialLeadsQuery && (
                <PartialLeadsTable query={partialLeadsQuery} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
